import { LayoutGrid, FolderTree, Database, UserSquare2, ShieldCheck } from "lucide-react";
import Dashboard from "../pages/dashboard/Dashboard";
import CategoryList from "../pages/category/CategoryList";
import MasterList from "../pages/master/MasterList";
import ArtistList from "../pages/artist/ArtistList";
import ProductList from "../pages/product/ProductList";
import LocationPanel from "../pages/location/LocationPanel";
import ReviewList from "../pages/review/ReviewList";
import SubAdminList from "../pages/subAdmin/SubAdminList";
import HomePageCMS from "../pages/homepage/HomePageCMS";
import SculptureCareCMS from "../pages/sculpture-care/SculptureCareCMS";
import JoinArtParksCMS from "../pages/join-art-parks/JoinArtParksPage";
import VideoGalleryCMS from "../pages/video-gallery/VideoGalleryCMS";
import AboutPageCMS from "../pages/about-page/AboutPageCMS";
import UserList from "../pages/users/UserList";
import OrderList from "../pages/order/OrderList";
import EnquiryList from "../pages/enquiry/EnquiryList";
import EnquirySettings from "../pages/enquiry-settings/EnquirySettings";
import FaqList from "../pages/faq/FaqList";
import NotificationList from "../pages/notification/NotificationList";
import {
  Package,
  MapPin,
  Star,
  Sparkles,
  Hammer,
  Image,
  Video,
  Info,
  Users,
  ShoppingBag,
  MessageSquare,
  HelpCircle,
  Bell,
  Mail,
} from "lucide-react";

export interface AppRoute {
  key: string;
  label: string;
  url?: string;
  icon?: React.ComponentType<any>;
  component?: React.ComponentType;
  children?: AppRoute[];
  hidden?: boolean;
  ignore?: number;
  type?: string;
  count?: number;
  badgeColor?: string;
  permission?: {
    module: string;
    action: string;
  };
  // Bypasses the permission check entirely - only user_type === "SUPER_ADMIN"
  // may see/access this route. Used for admin-account management itself,
  // which is never a grantable permission (a sub-admin must never be able
  // to manage or escalate other admin accounts).
  superAdminOnly?: boolean;
}

const routes: AppRoute[] = [
  // ─── OPERATIONS ────────────────────────────────────────────────────────────
  {
    key: "1",
    label: "Dashboard",
    url: "/",
    icon: LayoutGrid,
    component: Dashboard,
    type: "OPERATIONS",
    permission: { module: "DASHBOARD", action: "VIEW" },
  },
  {
    key: "user-management",
    label: "Users",
    url: "/users",
    icon: Users,
    component: UserList,
    type: "OPERATIONS",
    permission: { module: "USERS", action: "VIEW" },
  },
  {
    key: "order-management",
    label: "Orders",
    url: "/orders",
    icon: ShoppingBag,
    component: OrderList,
    type: "OPERATIONS",
    permission: { module: "ORDERS", action: "VIEW" },
  },
  {
    key: "enquiry-management",
    label: "Enquiries",
    url: "/enquiries",
    icon: MessageSquare,
    component: EnquiryList,
    type: "OPERATIONS",
    permission: { module: "ENQUIRIES", action: "VIEW" },
  },
  {
    key: "enquiry-settings",
    label: "Enquiry Settings",
    url: "/enquiry-settings",
    icon: Mail,
    component: EnquirySettings,
    type: "OPERATIONS",
    permission: { module: "CMS", action: "VIEW" },
  },
  {
    key: "notifications-management",
    label: "Notifications",
    url: "/notifications",
    icon: Bell,
    component: NotificationList,
    type: "OPERATIONS",
    hidden: true,
    permission: { module: "NOTIFICATIONS", action: "VIEW" },
  },
  {
    key: "2",
    label: "Category",
    url: "/category",
    icon: FolderTree,
    component: CategoryList,
    type: "OPERATIONS",
    permission: { module: "CATEGORY", action: "VIEW" },
  },
  {
    key: "3",
    label: "Masters",
    url: "/masters",
    icon: Database,
    component: MasterList,
    type: "OPERATIONS",
    permission: { module: "MASTERS", action: "VIEW" },
  },
  {
    key: "4",
    label: "Artists",
    url: "/artists",
    icon: UserSquare2,
    component: ArtistList,
    type: "OPERATIONS",
    permission: { module: "ARTISTS", action: "VIEW" },
  },
  {
    key: "5",
    // Peter, Oct 2026: "Could you change the word 'product' in the admin
    // side to Sculpture?" - label only, the URL stays /products so
    // existing bookmarks/links keep working.
    label: "Sculptures",
    url: "/products",
    icon: Package,
    component: ProductList,
    type: "OPERATIONS",
    permission: { module: "PRODUCTS", action: "VIEW" },
  },
  {
    key: "6",
    label: "Locations",
    url: "/locations",
    icon: MapPin,
    component: LocationPanel,
    type: "OPERATIONS",
    permission: { module: "LOCATIONS", action: "VIEW" },
  },
  {
    key: "sub-admin-management",
    label: "Admin Users",
    url: "/admin-users",
    icon: ShieldCheck,
    component: SubAdminList,
    type: "OPERATIONS",
    superAdminOnly: true,
  },
  {
    key: "7",
    label: "Reviews",
    url: "/reviews",
    icon: Star,
    component: ReviewList,
    type: "CMS & CONTENT",
    permission: { module: "REVIEWS", action: "VIEW" },
  },
  {
    key: "faq-management",
    label: "FAQ Management",
    url: "/faqs",
    icon: HelpCircle,
    component: FaqList,
    type: "CMS & CONTENT",
    permission: { module: "FAQS", action: "VIEW" },
  },
  {
    key: "8",
    label: "Home Page CMS",
    url: "/cms/homepage",
    icon: Sparkles,
    component: HomePageCMS,
    type: "CMS & CONTENT",
    permission: { module: "CMS", action: "VIEW" },
  },
  {
    key: "9",
    label: "Sculpture Care CMS",
    url: "/cms/sculpture-care",
    icon: Hammer,
    component: SculptureCareCMS,
    type: "CMS & CONTENT",
    permission: { module: "CMS", action: "VIEW" },
  },
  {
    key: "10",
    label: "Join Art Parks CMS",
    url: "/cms/join-art-parks",
    icon: Image,
    component: JoinArtParksCMS,
    type: "CMS & CONTENT",
    permission: { module: "CMS", action: "VIEW" },
  },
  {
    key: "11",
    label: "Video Gallery CMS",
    url: "/cms/video-gallery",
    icon: Video,
    component: VideoGalleryCMS,
    type: "CMS & CONTENT",
    permission: { module: "CMS", action: "VIEW" },
  },
  {
    key: "12",
    label: "About Page CMS",
    url: "/cms/about-page",
    icon: Info,
    component: AboutPageCMS,
    type: "CMS & CONTENT",
    permission: { module: "CMS", action: "VIEW" },
  },
];

export default routes;
